const HYDRATION_KEY = 'data-hydration-key';

function nodeKey(node) {
  if (!(node instanceof Element)) return '';
  return node.id || node.getAttribute(HYDRATION_KEY) || node.getAttribute('data-shopify-editor-block') || '';
}

function sameNode(current, next) {
  if (current.nodeType !== next.nodeType) return false;
  if (current.nodeType !== Node.ELEMENT_NODE) return true;
  if (current.tagName !== next.tagName) return false;
  const currentKey = nodeKey(current);
  const nextKey = nodeKey(next);
  return !currentKey || !nextKey || currentKey === nextKey;
}

function syncAttributes(current, next) {
  const preservedOpen =
    (current instanceof HTMLDialogElement || current instanceof HTMLDetailsElement) &&
    current.open &&
    !next.hasAttribute('declarative-open');

  for (const attribute of [...current.attributes]) {
    if (!next.hasAttribute(attribute.name)) current.removeAttribute(attribute.name);
  }
  for (const attribute of [...next.attributes]) {
    if (current.getAttribute(attribute.name) !== attribute.value) {
      current.setAttribute(attribute.name, attribute.value);
    }
  }

  if (preservedOpen) current.open = true;
}

function syncControl(current, next) {
  if (current instanceof HTMLInputElement && next instanceof HTMLInputElement) {
    if (current.type !== 'file') current.value = next.value;
    current.checked = next.checked;
    current.disabled = next.disabled;
  } else if (current instanceof HTMLTextAreaElement && next instanceof HTMLTextAreaElement) {
    current.value = next.value;
  } else if (current instanceof HTMLOptionElement && next instanceof HTMLOptionElement) {
    current.selected = next.selected;
  }
}

function morphNode(current, next) {
  if (!sameNode(current, next)) {
    current.replaceWith(next);
    return next;
  }

  if (current.nodeType === Node.TEXT_NODE || current.nodeType === Node.COMMENT_NODE) {
    if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue;
    return current;
  }

  syncAttributes(current, next);
  syncControl(current, next);
  morphChildren(current, next);
  return current;
}

function morphChildren(current, next) {
  const currentChildren = [...current.childNodes];
  const nextChildren = [...next.childNodes];
  const keyed = new Map();

  for (const child of currentChildren) {
    const key = nodeKey(child);
    if (key) keyed.set(`${child.nodeType}:${child.nodeName}:${key}`, child);
  }

  let cursor = current.firstChild;
  for (const nextChild of nextChildren) {
    const key = nodeKey(nextChild);
    const keyedMatch = key ? keyed.get(`${nextChild.nodeType}:${nextChild.nodeName}:${key}`) : null;
    let match = keyedMatch || cursor;

    if (keyedMatch && keyedMatch !== cursor) current.insertBefore(keyedMatch, cursor);

    if (!match) {
      current.appendChild(nextChild);
      continue;
    }

    if (!sameNode(match, nextChild)) {
      const unkeyedMatch = key
        ? null
        : [...current.childNodes].find((candidate) => candidate !== match && !nodeKey(candidate) && sameNode(candidate, nextChild));
      if (unkeyedMatch) {
        current.insertBefore(unkeyedMatch, match);
        match = unkeyedMatch;
      } else {
        current.insertBefore(nextChild, match);
        continue;
      }
    }

    const morphed = morphNode(match, nextChild);
    cursor = morphed.nextSibling;
  }

  const expected = new Set(nextChildren.map((node) => nodeKey(node)).filter(Boolean));
  while (cursor) {
    const following = cursor.nextSibling;
    const key = nodeKey(cursor);
    if (!key || !expected.has(key)) cursor.remove();
    cursor = following;
  }
}

function cssEscape(value) {
  return window.CSS?.escape ? window.CSS.escape(value) : value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function focusIdentity(element) {
  if (!(element instanceof HTMLElement)) return null;
  if (element.id) return `#${cssEscape(element.id)}`;
  const key = element.getAttribute(HYDRATION_KEY);
  if (key) return `[${HYDRATION_KEY}="${cssEscape(key)}"]`;
  const name = element.getAttribute('name');
  return name ? `${element.tagName.toLowerCase()}[name="${cssEscape(name)}"]` : null;
}

function morphHydration(current, next) {
  const targets = [next, ...next.querySelectorAll(`[${HYDRATION_KEY}]`)];
  for (const target of targets) {
    const key = target.getAttribute?.(HYDRATION_KEY);
    if (!key) continue;
    const selector = `[${HYDRATION_KEY}="${cssEscape(key)}"]`;
    const currentTarget = current.matches?.(selector) ? current : current.querySelector(selector);
    if (currentTarget) morphNode(currentTarget, target);
  }
}

export function morph(current, next, { mode = 'full' } = {}) {
  if (!(current instanceof Element) || !(next instanceof Element)) {
    throw new TypeError('Morph roots must be elements');
  }

  const active = current.contains(document.activeElement) ? focusIdentity(document.activeElement) : null;
  const selection = document.activeElement instanceof HTMLInputElement
    ? [document.activeElement.selectionStart, document.activeElement.selectionEnd]
    : null;

  if (mode === 'hydration') morphHydration(current, next);
  else morphNode(current, next);

  const focusTarget = active ? current.querySelector(active) : null;
  if (focusTarget instanceof HTMLElement) {
    focusTarget.focus({ preventScroll: true });
    if (selection && focusTarget instanceof HTMLInputElement) {
      focusTarget.setSelectionRange(selection[0], selection[1]);
    }
  }

  return current;
}
